import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { ApiConstants } from '../../shared/constants/api-constants';
import { EmployeesDetailsDto } from './models/employeesDetailsDto';
import { ScopeDetailsWithAvailability } from '../../shared/models/scopes/scope-details-with-availability';
import { SharedConstants } from '../../../app/shared/constants/shared-constants';
import { AssignUserSkillsRequestDto } from './models/assign-user-skills-request-dto';
import { UserSkillsByStatusDto } from './models/user-skills-by-status';
import { AssignedSkillResponseDto } from './models/assigned-skill-response-dto';
import { AuditTrialDetails } from '../../../app/shared/models/audits/audit-trial-details';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(private httpClient: HttpClient) { }

  getUserByPuid(puid:string):Observable<any>{
    return this.httpClient.get(ApiConstants.GetUserProfile_Api.replace("{puid}",puid));
  }

  addAttachment(puid:string, attachmentDetails:object):Observable<any>{
    return this.httpClient.post(ApiConstants.UserProfileAddAttachment_Api.replace("{puid}",puid), attachmentDetails);
  }

  removeAttachment(puid:string, attachmentId:string):Observable<any>{
    return this.httpClient.delete(ApiConstants.UserProfileRemoveAttachment_Api.replace("{puid}",puid)+attachmentId);
  }

  getAttachmentsForUser(puid:string):Observable<any>{
    return this.httpClient.get(ApiConstants.GetAttachmentsForUser_Api.replace("{puid}",puid));
  }

  downloadFile(fileId: string)
    : Observable<any> {
    return this.httpClient.get(ApiConstants.Files_Download_Api + `${fileId}`);
  }

  deleteFile(fileId: string): Observable<any> {
    return this.httpClient.delete<any>(
      `${ApiConstants.Files_Delete_Api}${fileId}`
    );
  }

  uploadFile(file: File, foldername: string): Observable<any> {
  const formData = new FormData();
  formData.append(file.name, file);
  return this.httpClient.post(
    ApiConstants.Files_Upload_Api + foldername, formData
  ).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = SharedConstants.File_Upload_Failed_Error;
      
      if (error.status === 400 && error.error?.errorMessage) {
        // Handle duplicate file error
        errorMessage = error.error.errorMessage;
      } else if (error.status === 401) {
        errorMessage = SharedConstants.UnAuthorized_File_Upload_Error;
      } else if (error.status === 500 && error.error?.errorMessage) {
        errorMessage = error.error.errorMessage;
      }
      
      return throwError(() => errorMessage);
    })
  );
}

  getUserProfileAuthentication() : Observable<any> {
    return this.httpClient.get(ApiConstants.UserProfileAuthorization_Api);
  }

  geteQMSScopesForUserProfile(): Observable<ScopeDetailsWithAvailability[]> {
    return this.httpClient.get<ScopeDetailsWithAvailability[]>(ApiConstants.EQMSScopesLookupForUserProfile_Api);
  }
  getAllUsers(): Observable<any> {
    return this.httpClient.get(ApiConstants.Users_Api);
  }
  getSecurityPermissions(): Observable<any> {
    return this.httpClient.get(ApiConstants.Users_Security_Permission_Api);
  }
  getUserProfileInformationForEdit(puid:any): Observable<any> {
    return this.httpClient.get(ApiConstants.Users_Profile_Data_Api.replace("{puid}",puid));
  }

  updateUserProfile(puid:any,profileInformation:any):Observable<any>{
    return this.httpClient.put(ApiConstants.Update_Profile_Data_Api.replace("{puid}",puid), profileInformation);
  }

  getUsersForMyEmployeesProfile(puid : string) : Observable<EmployeesDetailsDto[]>{
    return this.httpClient.get<EmployeesDetailsDto[]>(ApiConstants.GetUsersForMyEmployeesProfile_Api.replace("{puid}",puid))
  }

  assignSkillsToUser(request: AssignUserSkillsRequestDto): Observable<any> {
    return this.httpClient.post(ApiConstants.Assign_Skills_To_User_Api, request);
  }
  /// <summary>
  /// Fetches user-assigned skills grouped by status for a specific user.
  /// Returns skills organized into: Earned, PendingAcknowledgement, PendingVerification, and Removed.
  /// </summary>
  /// <param name="userPuid">The user's unique identifier (PUID)</param>
  /// <returns>UserSkillsByStatusDto containing skills grouped by their status</returns>
  getUserAssignedSkills(userPuid: string): Observable<UserSkillsByStatusDto> {
    return this.httpClient.get<UserSkillsByStatusDto>(
      `${ApiConstants.UserAssignedSkills_Api}/${userPuid}`
    );
  }

  getAssignedSkillsForUser(puid: string): Observable<AssignedSkillResponseDto[]> {
    return this.httpClient.get<AssignedSkillResponseDto[]>(
      ApiConstants.Get_Assigned_Skills_For_User_Api.replace("{puid}", puid)
    );
  }

  /**
   * Unassign a skill from a user (Flow A - Manager removal)
   * @param userAssignedSkillId - The ID of the user-assigned skill
   * @param removalReason - Reason for removing the skill
   * @returns Observable of the unassignment response
   */
  unassignSkillAsManager(userAssignedSkillId: string, removalReason: string): Observable<any> {
    return this.httpClient.put(
      ApiConstants.Unassign_Skill_As_Manager_Api.replace('{id}', userAssignedSkillId),
      { removalReason }
    );
  }

  /**
   * Revoke training occurrences for skill unassignment (Flow B - Auto-assigned removal)
   * @param puid - User PUID
   * @param request - Request containing training item IDs, skill ID, and reason
   * @returns Observable of the revocation response
   */
  revokeTrainingOccurrences(puid: string, request: any): Observable<any> {
    return this.httpClient.post(
      ApiConstants.Revoke_Training_For_Skill_Unassignment_Api.replace('{puid}', puid),
      request
    );
  }

  getUserProfileAuditTrail(puid: string):Observable<AuditTrialDetails[]> {
    return this.httpClient.get<AuditTrialDetails[]>(
      ApiConstants.UserProfileAuditTrail_Api.replace("{puid}", puid)
    );
  }
}
